import { ApiError, OrderWithContext,  Promotion, OrderApiException, OrderErrorCode  } from '@/models';
import {
  GetRewardOffersResponse,
  Reward,
  GetRewardOfferTypesResponse,
  RewardStoreErrorCode,
  GetExtrasCampaignsResponse,
  GetExtrasAchievementsResponse,
  AchievementTitle,
  BagOffersResponse,
  PurchasedReward,
  Achievement,
  AchievementYear,
  OptIntoCampaignResponse,
} from '@/models/reward';
import { AppState } from '@/store';
import { MUTATIONS } from '@/store/reward';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { Store } from 'vuex';
import Vue from 'vue';
import { AnalyticsService } from '../analytics/analytics.service';
import { OrderService } from '../order/order.service';
import { BagService } from '../bag/bag.service';
import { UiService } from '../ui/ui.service';
import { ApiService } from '../api/api.service';
import { FeatureFlagsService } from '../feature-flags/feature-flags.service';
import { Logger } from '../logger';
import { ButtonType } from '@/components/CmgButton/cmg-button';

export interface RewardService {
  getFavoriteReward(): Reward | undefined;
  getPointsBalance(): number;
  getNumRedeemableRewards(): number;
  getNumRedeemableRewardsStr(): string;
  getProgressPercentage(): number;
  getRewardStarUrl(reward: Reward, hover: boolean): string;
  getRewardOffers(): Promise<any>;
  getCyobOffers(): Promise<any>;
  getBagOffers(): Promise<any>;
  getRewardStoreOfferTypes(): Promise<any>;
  getExtrasAchievements(): Promise<any>;
  getExtrasCampaigns(): Promise<any>;
  getTwitterAchievementUrl(achievementTitle: string, baseUrl: string): string;
  isSessionMError(error: AxiosError): boolean;
  isSparkflyError(error: AxiosError): boolean;
  setShouldShowRefresh(value: boolean): void;
  favoriteReward(offerId: string): Promise<any>;
  unfavoriteReward(offerId: string): Promise<any>;
  purchaseReward(offerId: string): Promise<PurchasedReward | null>;
  purchaseAndApplyReward(offerId: string, order: OrderWithContext): Promise<any>;
  toggleFavoriteStatus(reward: Reward): void;
  clearRewards(): void;
  optIntoCampaign(campaignId: number): Promise<OptIntoCampaignResponse | null>;
  addPromotionToBag(promotion?: Promotion, purchasedReward?: PurchasedReward, onCloseModal?: () => void, onLoading?: (loading: boolean) => void, sendRewardsAddedToBagAnalytics?: () => void): Promise<void>;
}

export class AppRewardService extends ApiService implements RewardService {
  pendingCalls = 0;

  constructor(store: Store<AppState>, httpClient: AxiosInstance, logger: Logger, private featureFlags: FeatureFlagsService, private order: OrderService, private bag: BagService, private ui: UiService, analytics: AnalyticsService) {
    super(store, httpClient, logger, analytics);
  }

  get isUpdatedContextualRewardsBagUpdatesEnabled(): boolean {
    return this.store.state.featureFlags.featureFlags.isUpdatedContextualRewardsBagUpdatesEnabled;
  }

  
  isRewardsStoreOnline(): boolean {
    const flags = this.store.state.featureFlags;

    if (flags.orderingStatus && !flags.orderingStatus.isRewardsExchangeOnline) {
      return false;
    }

    return true;
  }

  async getRewardStoreOfferTypes() {
    await this.featureFlags.ensureApimFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve();
    }

    return this.httpClient
      .get(`/rewardstore/v2/rewardstore/offerTypes`)
      .then((res: AxiosResponse<GetRewardOfferTypesResponse>) => {
        this.store.commit(MUTATIONS.SET_REWARDSTORE_OFFERTYPES, res.data);
        this.store.commit(MUTATIONS.SET_UNREACHABLE, false);
      })
      .catch(e => this.handleError('getRewardStoreOfferTypes', e));
  }

  async getBagOffers(){
    await this.featureFlags.ensureApimFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve();
    }

    const cmgIds: string[] = [];
    const order: OrderWithContext | null = this.store.getters.currentOrder;

    if (order && order.order.meals) {
      order.order.meals.forEach(meal => {
        if (meal.entrees && meal.entrees.length > 0) {
          meal.entrees.forEach(entree => {
            cmgIds.push(entree.menuItemId);
          });
        }
        if (meal.sides && meal.sides.length > 0) {
          meal.sides.forEach(side => {
            cmgIds.push(side.menuItemId);
          });
        }
        if (meal.drinks && meal.drinks.length > 0 && this.isUpdatedContextualRewardsBagUpdatesEnabled) {
          meal.drinks.forEach(drink => {
            cmgIds.push(drink.menuItemId);
          });
        }
      });
    }

    if (cmgIds.length == 0) {
      return;
    }

    return this.httpClient
      .get(`/rewardstore/v2/rewardstore/bagOffers?CmgIds=${cmgIds.join()}&AvailablePoints=${this.getPointsBalance()}&Channel=web`)
      .then((r: AxiosResponse<BagOffersResponse>) => {
        this.store.commit(MUTATIONS.SET_BAG_OFFERS, r.data.rewardStoreOffers);
        this.store.commit(MUTATIONS.SET_UNREACHABLE, false);
        return r.data;
      })
      .catch(e => this.handleError('getBagOffers', e));
  }

  async getRewardOffers() {
    await this.featureFlags.ensureApimFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve();
    }

    return this.httpClient
      .get(`/rewardstore/v2/rewardstore/web`)
      .then((res: AxiosResponse<GetRewardOffersResponse>) => {
        this.store.commit(MUTATIONS.SET_REWARDS, res.data.rewardStoreOffers);
        this.store.commit(MUTATIONS.SET_UNREACHABLE, false);
        this.store.commit(MUTATIONS.SET_NUM_REDEEMABLE_REWARDS_STR, this.getNumRedeemableRewardsStr());
        window.dispatchEvent(new Event('cmg-vuex-rewards-updated'));
      })
      .catch(e => this.handleError('getRewardOffers', e));
  }

  async getCyobOffers() {
    await this.featureFlags.ensureApimFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve();
    }

    return this.httpClient
      .get(`/rewardstore/v2/rewardstore/birthdayOffers/web`)
      .then((res: AxiosResponse<GetRewardOffersResponse>) => {
        this.store.commit(MUTATIONS.SET_CYOB_REWARDS, res.data.rewardStoreOffers);
        this.store.commit(MUTATIONS.SET_UNREACHABLE, false);
      })
      .catch(e => this.handleError('getCyobOffers', e));
  }

  async favoriteReward(offerId: string) {
    await this.featureFlags.ensureApimFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve();
    }

    return this.httpClient
      .post(`/rewardstore/v2/rewardstore/favorite/${offerId}`)
      .then(() => this.store.commit(MUTATIONS.SET_UNREACHABLE, false))
      .catch(e => this.handleError('favoriteReward', e));
  }

  async unfavoriteReward(offerId: string) {
    await this.featureFlags.ensureApimFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve();
    }

    return this.httpClient
      .delete(`/rewardstore/v2/rewardstore/favorite/${offerId}`)
      .then(() => this.store.commit(MUTATIONS.SET_UNREACHABLE, false))
      .catch(e => this.handleError('unfavoriteReward', e));
  }

  async purchaseReward(offerId: string): Promise<PurchasedReward | null>  {
    await this.featureFlags.ensureApimFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve(null);
    }

    return this.httpClient
      .post(`/rewardstore/v2/rewardstore/offer/${offerId}`)
      .then((r: AxiosResponse<PurchasedReward>) => {
        this.store.commit(MUTATIONS.SET_UNREACHABLE, false)
        return r.data;
      })
      .catch(e => this.handleError('purchaseReward', e));
  }

  async purchaseAndApplyReward(offerId: string, order: OrderWithContext) {
    await this.featureFlags.ensureApimFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve();
    }

    return this.httpClient
      .post(`/rewardstore/v2/rewardstore/offer/${offerId}/${order.order.orderId}`, null, {
        headers: {
          'If-Match': order.etag,
        }
      })
      .then(() => this.store.commit(MUTATIONS.SET_UNREACHABLE, false))
      .catch(e => this.handleError('purchaseAndApplyReward', e));
  }

  async getExtrasCampaigns() {
    await this.featureFlags.ensureApimFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve();
    }

    return this.httpClient
      .get(`/rewardstore/v2/rewardstore/extrasCampaigns`)
      .then((res: AxiosResponse<GetExtrasCampaignsResponse>) => {
        // Filter out chipotleUChallenge campaigns
        const filteredCampaigns = res.data.campaigns.filter(
          campaign => campaign.campaignType?.toLowerCase() !== 'chipotleuchallenge'
        );

        this.store.commit(MUTATIONS.SET_EXTRAS_CAMPAIGNS, filteredCampaigns);

        if (res.data.groupCampaigns) {
          // Filter group campaigns that don't have chipotleUChallenge campaigns
          const filteredGroupCampaigns = res.data.groupCampaigns.filter(
            groupCampaign => groupCampaign.campaigns[0].campaignType?.toLowerCase() !== 'chipotleuchallenge'
          );
          filteredGroupCampaigns.forEach((groupCampaign) => {
            groupCampaign.endDate = groupCampaign.campaigns[0].endDate;
            groupCampaign.extraPoints = groupCampaign.campaigns[0].extraPoints;
          });
          this.store.commit(MUTATIONS.SET_EXTRAS_GROUP_CAMPAIGNS, filteredGroupCampaigns);
        } else {
          this.store.commit(MUTATIONS.SET_EXTRAS_GROUP_CAMPAIGNS, []);
        }

        this.store.commit(MUTATIONS.SET_UNREACHABLE, false);
      })
      .catch(e => this.handleError('getExtrasCampaigns', e));
  }

  async getExtrasAchievements() {
    await this.featureFlags.ensureApimFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve();
    }

    await this.httpClient
      .get(`/rewardstore/v2/rewardstore/extrasAchievements?`)
      .then((res: AxiosResponse<GetExtrasAchievementsResponse>) => {
        const achievements: Achievement[] = [];
        const achievementYears: AchievementYear[] = [];

        if(res.data.achievementYears.length > 0) {
          res.data.achievementYears.forEach(year => {
            const spliceAchievementYear = achievementYears.find(x => x.badgeYear === year.badgeYear);
            if(spliceAchievementYear) {
              spliceAchievementYear.badges.forEach(badge => {
                const sameBadge = spliceAchievementYear.badges.find(x => x.achievement.achievementId === badge.achievement.achievementId);
                if(sameBadge){
                  sameBadge.earnedCount++;
                } else {
                  spliceAchievementYear.badges.push(badge);
                  achievements.push(badge.achievement);
                } 
              });
            } else {
              achievementYears.push(year);
              year.badges.forEach(badge => achievements.push(badge.achievement));    
            }
          });
          this.store.commit(MUTATIONS.SET_EXTRAS_ACHIEVEMENT_YEARS, achievementYears);
          this.store.commit(MUTATIONS.SET_EXTRAS_ACHIEVEMENTS, achievements);
          this.store.commit(MUTATIONS.SET_TOTAL_EXTRA_POINTS, this.store.state.reward.totalExtraPoints + res.data.totalPointsEarnedFromExtras);
          this.store.commit(MUTATIONS.SET_UNREACHABLE, false);
        }
      })
      .catch(e => this.handleError('getExtrasAchievementsByPage', e))
  }

  getPointsBalance(): number {
    return this.store.state.customer.rewardPoints?.currentPointsBalance ?? 0;
  }

  getNumRedeemableRewards(): number {
    const pointsBalance = this.getPointsBalance();

    return this.store.state.reward.rewards
      .filter(r => r.points <= pointsBalance)
      .length;
   }

  getNumRedeemableRewardsStr(): string {
    const numRewards = this.getNumRedeemableRewards();
    return `${numRewards} Reward${numRewards > 1 ? 's' : ''}`;
  }

  getProgressPercentage(): number {
    return this.getFavoriteReward()
      ? (this.getPointsBalance() / this.getFavoriteReward()!.points) * 100
      : 0;
  }

  getFavoriteReward(): Reward | undefined {
    return this.store.state.reward.rewards.find(r => r.isFavorite);
  }

  getRewardStarUrl(reward: Reward, hover: boolean): string {
    if (!reward.isFavorite && hover) {
      return this.store.state.cms.images.icons.rewardStarHover.default;
    }

    return reward.isFavorite
      ? this.store.state.cms.images.icons.rewardStarSelected.default
      : this.store.state.cms.images.icons.rewardStar.default;
  }

  setShouldShowRefresh(value: boolean): void {
    this.store.commit(MUTATIONS.SET_SHOULD_SHOW_REFRESH, value);
  }

  async toggleFavoriteStatus(reward: Reward) {
    // preserve original isFavorite status
    const isFavorite = reward.isFavorite;

    // toggle status for immediate visual feedback
    reward.isFavorite = !reward.isFavorite;

    // toggle status of otherFavoriteReward for immediate visual feedback
    const otherFavoriteReward = this.store.state.reward.rewards.find(r => r.id != reward.id && r.isFavorite);
    if (otherFavoriteReward) {
      otherFavoriteReward.isFavorite = false;
      this.pendingCalls++;
      await this.unfavoriteReward(otherFavoriteReward.id);
      this.pendingCalls--;
    }

    // If it is favorited, unfavorite it
    // If it is not favorited, favorite it
    // If there is another favorited reward, unfavorite it
    this.pendingCalls++;
    isFavorite
      ? await this.unfavoriteReward(reward.id)
      : await this.favoriteReward(reward.id)
    this.pendingCalls--;

    // Don't fetch latest reward offers until after the API calls stop writing to Gigya
    // Otherwise with multiple clicks the user will see unexpected UI behavior.
    if (this.pendingCalls == 0) {
      this.getRewardOffers();
    }
  }

  clearRewards(): void {
    this.store.commit(MUTATIONS.SET_REWARDS, []);
  }

  async optIntoCampaign(campaignId: number): Promise<OptIntoCampaignResponse | null> {
    await this.featureFlags.ensureApimFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve(null);
    }

    return this.httpClient
      .post(`/rewardstore/v2/rewardstore/optInto?campaignId=${campaignId}`)
      .then((r: AxiosResponse<OptIntoCampaignResponse>) => {
        return r.data;
      })
      .catch(e => this.handleError('optIntoCampaign', e));
  }

  isSessionMError(error: AxiosError): boolean {
    const errorSet = new Set([
      RewardStoreErrorCode.RewardStoreUnavailable,
      RewardStoreErrorCode.CustomerRecordUnavailable,
      RewardStoreErrorCode.CustomerNotEnrolledInLoyalty,
      RewardStoreErrorCode.SessionMCustomerNotFound,
      RewardStoreErrorCode.InsufficientPoints,
    ]);

    const axiosApiError = error as AxiosError<ApiError, any>;
    if (axiosApiError && axiosApiError.response && axiosApiError.response.data) {
      return errorSet.has(axiosApiError.response.data.errorCode as RewardStoreErrorCode);
    }

    return false;
  }

  isSparkflyError(error: AxiosError): boolean {
    const errorSet = new Set([
      RewardStoreErrorCode.SparkflyUnavailable,
      RewardStoreErrorCode.SparkflyOfferNotFound,
    ]);

    const axiosApiError = error as AxiosError<ApiError, any>;
    if (axiosApiError && axiosApiError.response && axiosApiError.response.data) {
      return errorSet.has(axiosApiError.response.data.errorCode as RewardStoreErrorCode);
    }

    return false;
  }

  getTwitterAchievementUrl(achievementTitle: string, baseUrl: string): string {
    switch (achievementTitle.toLowerCase()) {
      case AchievementTitle.BigSipper:
        return baseUrl + '/big-sipper.html';
      case AchievementTitle.FirstToTryBrisket:
        return baseUrl + '/first-to-try-brisket.html';
      case AchievementTitle.InTheBag:
        return baseUrl + '/in-the-bag.html';
      case AchievementTitle.LunchLegend:
        return baseUrl + '/lunch-legend.html';
      case AchievementTitle.NationalGuacDay:
        return baseUrl + '/national-guac-day.html';
      case AchievementTitle.TheDoubleUp:
        return baseUrl + '/the-double-up.html';
      case AchievementTitle.Threepeat:
        return baseUrl + '/three-peat.html';
    }

    return baseUrl;
  }

  async addPromotionToBag(promotion?: Promotion, purchasedReward?: PurchasedReward, onCloseModal?: () => void, onLoading?: (loading: boolean) => void, sendRewardsAddedToBagAnalytics?: () => void): Promise<void> {
    this.ui.setLastSelectedElement('bag');

    const promotionCode = promotion?.promotionCode || purchasedReward?.promotionCode || '';
    const promotionName = promotion?.promotionName || purchasedReward?.promotionName || '';

    let order = this.order.getCurrentOrder();

    onLoading?.(true);

    if (!order) {
      if (this.store.state.restaurant.selectedRestaurant) {
        order = await this.order.createOrder(this.store.state.restaurant.selectedRestaurant!.restaurantNumber);
        this.setLoadingStatus(false);
      } else {
        this.setLoadingStatus(false);
        onCloseModal?.();
        this.ui.setModalVisibility('findAChipotle', true, {
          onClose: async () => {
            const restaurant = this.store.state.restaurant.selectedRestaurant;
            if (!restaurant) {
              return;
            }

            try {
              this.setLoadingStatus(true);
              order = await this.order.createOrder(restaurant.restaurantNumber);

              if (order && promotionCode) {
                await this.order.applyDiscountToOrder(order, promotionCode);
                this.bag.setBagVisibility(true);
                await this.order.throwIfDiscountErrorCode(OrderErrorCode.PromotionCodeLocked);
                sendRewardsAddedToBagAnalytics?.();
            }
            } catch (e: any) {
              await this.handleAddToBagException(e, order, promotion, purchasedReward);
            } finally {
              this.setLoadingStatus(false);
            }
          }
        });

        this.ui.setModalVisibility('toast', true, {
          body: 'Before you order, we gotta find your Chipotle first.'.toUpperCase(),
          bodyOnly: true,
        });
        Vue.nextTick(() => this.ui.focusFirstDiv('.cmg-modal-container'));
        return;
      }
    }

    if (!order) {
      return;
    }

    try {
      const isRewardInBag = order.discounts?.some(d => d.promotionCode === promotionCode);
      if (isRewardInBag) {
        await this.order.removeDiscountFromOrder(order, promotionCode);
        this.bag.setBagVisibility(true);
        Vue.nextTick(() => this.ui.focusDiv('.fullModal'));
      } else {
        await this.order.applyDiscountToOrder(order, promotionCode);
        this.bag.setBagVisibility(true);
        Vue.nextTick(() => {
          Vue.nextTick(() => this.ui.focusDiv('.fullModal'));
          this.ui.setLastSelectedElement('bag');
        });
        await this.order.throwIfDiscountErrorCode(OrderErrorCode.PromotionCodeLocked);
      }
    } catch (e: any) {
      await this.handleAddToBagException(e, order, promotion, purchasedReward);
    } finally {
      this.setLoadingStatus(false);
      onCloseModal?.();
    }
  }

  private handleError(operationName: string, e: AxiosError) {
    if (e.response) {
      this.store.commit(MUTATIONS.SET_UNREACHABLE, e.response.status >= 400);
    }

    return this.logAndThrowApiError(operationName, e);
  }

  private setLoadingStatus(loading: boolean) {
    this.ui.setAppBlur(loading);
    this.ui.setAppPepperVisibility(loading);
  }

  private async handleAddToBagException(e: any, order?: any, promotion?: Promotion, purchasedReward?: PurchasedReward): Promise<void> {
    if (!(e instanceof OrderApiException)) {
      throw e;
    }

    const error = e.errors[0];
    if (!error) {
      throw e;
    }

    const promotionCode = promotion?.promotionCode || purchasedReward?.promotionCode || '';
    const promotionName = promotion?.promotionName || purchasedReward?.promotionName || '';

    if (!order || !promotionCode || !promotionName) {
      this.ui.setModalVisibility('globalErrorToast', true);
      return;
    }

    switch (error.errorCode) {
      case OrderErrorCode.PromotionCodeLocked:
        this.ui.setModalVisibility('basicError', true, {
                header: 'Aw Shucks',
                content: `<p>Looks like that promo is already in use. You can continue without it or try another code.</p><br/><span class='promo-missing-item-text'>${promotionName}</span>`,
                options: [
                  {
                    text: 'Okay',
                    type: ButtonType.Primary,
                    onClick: () => {
                      this.ui.setModalVisibility('basicError', false);
                    },
                  },
                ],
              });
        return;
      case OrderErrorCode.RewardExceedsLimit:
      case OrderErrorCode.PromotionExceedsMaxAllowedDiscounts:
        const existingDiscount = order.discounts?.[0];

        const existingName = existingDiscount.promotionName || 'Existing reward';
        const existingCode = existingDiscount.promotionCode;

        const replaceOption = {
          text: 'Replace',
          type: ButtonType.Primary,
          isLoading: false,
          onClick: async () => {

            if (!order) {
              this.ui.setModalVisibility('basicError', false);
              this.ui.setModalVisibility('globalErrorToast', true);
              return;
            }
            replaceOption.isLoading = true;

            try {
              await this.order.removeDiscountFromOrder(order, existingCode);
              await this.order.applyDiscountToOrder(order, promotionCode);

              this.ui.setModalVisibility('basicError', false);
              this.bag.setBagVisibility(true);
              Vue.nextTick(() => {
                Vue.nextTick(() => this.ui.focusDiv('.fullModal'));
                this.ui.setLastSelectedElement('bag');
              });
            } catch (replaceError: any) {
              this.ui.setModalVisibility('basicError', false);
              await this.handleAddToBagException(replaceError, order);
            } finally {
              replaceOption.isLoading = false;
            }
          },
        }

        this.ui.setModalVisibility('basicError', true, {
          header: `USE ${promotionName}`,
          content: `You can only use one promo or reward at a time. If you continue this will replace the ${existingName} reward or promo you already added.`,
          options: [
            {
              text: 'Nevermind',
              type: ButtonType.Secondary,
              onClick: () => {
                this.ui.setModalVisibility('basicError', false);
              },
            },
             replaceOption
          ],
        });

        Vue.nextTick(() => {
          setTimeout(() => {
            this.ui.nextFocus('reward-heading');
          }, 6000);
        });
        break;
    }
  }
}
